import React, { useEffect, useState } from "react";
import { benefitsApi, getImageUrl } from "../api";
import ImageLoader from "./common/ImageLoader";
import "./AdminBenefits.css";

const AdminBenefits = () => {
  const [benefits, setBenefits] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBenefits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBenefits = async () => {
    try {
      const res = await benefitsApi.getAll();
      setBenefits(res.data || []);
    } catch (err) {
      console.error("Error fetching benefits:", err);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !imageFile) {
      return alert("Title, description, and image are required.");
    }

    setLoading(true);
    const data = new FormData();
    data.append("title", formData.title);
    data.append("description", formData.description);
    data.append("link", formData.link);
    data.append("image", imageFile);

    try {
      await benefitsApi.create(data);
      alert("✅ Member benefit added successfully!");
      setFormData({ title: "", description: "", link: "" });
      setImageFile(null);
      setPreviewUrl(null);
      e.target.reset();
      fetchBenefits();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("❌ Failed to upload benefit");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this member benefit permanently?")) return;

    try {
      await benefitsApi.delete(id);
      fetchBenefits();
      alert("🗑️ Benefit deleted!");
    } catch (err) {
      console.error("Delete error:", err);
      alert("❌ Delete failed");
    }
  };

  return (
    <div className="admin-benefits-wrapper">
      <div className="admin-panel-header">
        <div className="panel-title-group">
          <i className="fas fa-gift title-icon"></i>
          <div>
            <h2>Manage Chapter Member Benefits</h2>
            <p className="panel-subtitle">Showcase exclusive perks, resources, workshops, and ACM global advantages</p>
          </div>
        </div>
      </div>

      <div className="benefits-layout-grid">
        {/* Form Card */}
        <div className="benefit-form-card">
          <h3 className="sub-heading-tag"><i className="fas fa-plus-circle"></i> Add New Perk</h3>
          <form className="admin-glass-form" onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label>Benefit Title *</label>
              <input
                type="text"
                name="title"
                placeholder="e.g. ACM Digital Library Access"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="admin-form-group">
              <label>Perk Details &amp; Summary *</label>
              <textarea
                name="description"
                placeholder="Describe how members can utilize this benefit..."
                value={formData.description}
                onChange={handleFormChange}
                rows="4"
                required
              />
            </div>

            <div className="admin-form-group">
              <label><i className="fas fa-link"></i> External Resource Link (Optional)</label>
              <input
                type="url"
                name="link"
                placeholder="https://dl.acm.org/..."
                value={formData.link}
                onChange={handleFormChange}
              />
            </div>

            <div className="admin-form-group">
              <label><i className="fas fa-image"></i> Perk Icon / Thumbnail *</label>
              <input type="file" accept="image/*" onChange={handleImageChange} required />
              {previewUrl && (
                <div className="benefit-preview-box">
                  <img src={previewUrl} alt="Preview" />
                </div>
              )}
            </div>

            <button type="submit" className="admin-btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-circle-notch fa-spin"></i> Adding...
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i> Save Benefit
                </>
              )}
            </button>
          </form>
        </div>

        {/* Benefits Cards Display */}
        <div className="benefits-cards-column">
          <h3 className="sub-heading-tag">
            <i className="fas fa-award"></i> Active Member Perks ({benefits.length})
          </h3>

          {benefits.length === 0 ? (
            <p className="no-items-text">No benefits configured yet.</p>
          ) : (
            <div className="benefits-cards-grid">
              {benefits.map((b) => (
                <div key={b.id} className="admin-benefit-card">
                  <div className="benefit-card-media">
                    <ImageLoader src={getImageUrl(b.imgSrc || b.imagePath)} alt={b.title} />
                  </div>
                  <div className="benefit-info-body">
                    <h4>{b.title}</h4>
                    <p>{b.description}</p>
                    <div className="benefit-card-footer">
                      {b.link ? (
                        <a
                          href={b.link}
                          target="_blank"
                          rel="noreferrer"
                          className="benefit-link-btn"
                        >
                          <i className="fas fa-external-link-alt"></i> Learn More
                        </a>
                      ) : (
                        <span></span>
                      )}
                      <button
                        className="delete-benefit-btn"
                        onClick={() => handleDelete(b.id)}
                        title="Delete benefit"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBenefits;